import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import moment from 'moment';

import {
  selectDetails,
  setDirectDebitError
} from 'state/direct-debit/direct-debit-slice';
import { SchemaValidationError } from 'errors/SchemaValidationError';
import { actionUrl } from 'config/static-content';
import { validateBankAccountThunk } from 'state/direct-debit/direct-debit-thunks';
import { usePartner } from 'components/app/routes/usePartner';
import { PARTNERS } from 'config/app-config';
import { selectAccessToken } from 'state/azure-active-directory/aad-slice';

import payloadSchema from 'api/schemas/validate-bank-account-payload-schema.json';

export const useValidateBankAccount = () => {
  const dispatch = useDispatch();
  const details = useSelector(selectDetails, shallowEqual);
  const accessToken = useSelector(selectAccessToken, shallowEqual);

  const { partnerName } = usePartner();

  const agentSalesPaths = [
    PARTNERS.VOICESALES_ACQUISITION,
    PARTNERS.VOICESALES_HOMEMOVE,
    PARTNERS.VOICESALES_WINBACK
  ];

  let requestConfig = {};
  const isMSALTokenRequired = agentSalesPaths.includes(partnerName);
  if (isMSALTokenRequired) {
    requestConfig = {
      headers: {
        Authorization: accessToken
      }
    };
  }

  const {
    isCommercial,
    firstName,
    lastName,
    birthDate,
    address,
    companyName,
    companyRegistrationNumber,
    accountNumber,
    sortCode
  } = details;
  const {
    flatNumber = '',
    houseNumber = '',
    houseName = '',
    addressLine1 = '',
    addressLine2 = '',
    dependentLocality = '',
    doubleDependentLocality = '',
    postalTown = '',
    county = '',
    postcode = '',
    country = ''
  } = address ?? {};
  const dateTimeBirthDate = moment.utc(birthDate, 'YYYY-MM-DD').toISOString();

  return async () => {
    const payload = {
      data: {
        type: 'bank-accounts',
        attributes: {
          id: '',
          number: accountNumber,
          'sort-code': sortCode,
          'first-name': firstName,
          surname: lastName,
          'date-of-birth': dateTimeBirthDate,
          'validate-address': true,
          'perform-organization-check': true,
          'manual-address': {
            'flat-number': flatNumber,
            'house-number': houseNumber,
            'house-name': houseName,
            'address-line1': addressLine1,
            'address-line2': (
              addressLine2 +
              ' ' +
              dependentLocality +
              ' ' +
              doubleDependentLocality
            ).trim(),
            'postal-town': postalTown,
            county: county,
            postcode: postcode,
            country: country
          },
          ...(isCommercial && {
            'organization-name': companyName,
            'company-registration-number': companyRegistrationNumber
          })
        }
      }
    };

    const ajv = new Ajv({ strict: false });
    addFormats(ajv);
    const isValidPayload = ajv.validate(payloadSchema, payload);

    if (!isValidPayload) {
      const error = new SchemaValidationError({
        title: 'Error',
        message: `We cannot set up your Direct Debit at this time. Please contact ${actionUrl.support}`,
        payload,
        schema: payloadSchema,
        ajvErrors: ajv.errors
      });

      dispatch(setDirectDebitError([error.toJson()]));
      throw error;
    }

    return await dispatch(validateBankAccountThunk(payload, requestConfig));
  };
};
